import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Form, Input, Button, message, Upload, Select, Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import {
  getProductLogoUploadUrl,
  campaignSaveApi,
  campaignSaveNextApi,
  getCampaignDetailApi,
  getActiveCampaignDetailApi,
  getCampaignDetailNoSessionApi,
  saveCampaignNoSessionApi,
} from "@/api/createCampaignApi";
import ProductLogo from "@/components/ProductLogo";
import axios from "axios";
import { useQuery } from "@/hooks/useQuery";
import { useNavigate } from "react-router-dom";
import { RightArrowIcon } from "@/components/icons";
import { clientTypeOptions } from "../../config";
import { genderOptions, ageOptions, ProductPhysicalSelectEnum } from "./config";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { useClerk, useUser } from "@clerk/clerk-react";
import GenerateAnimation from "@/components/GenerateAnimation";
import { CreateCampaignStageEnum, ProductType } from "../../config";
import HeadContainer from "@/components/HeadContainer";
import ProgressTimeLine from "../ProgressTimeLine";
import AiAgent from "@/components/AiAgent";
import { CampaignMarketingStrategy } from "../DevelopStrategy/config";
import { CampaignStatusEnum } from "@/components/campaignCard/config";
import ProductDelivery from "../otherInformation/ProductDelivery";
import CoreSellingPointsForm from "@/components/CoreSellingPointsForm";
import CoreAudiencesForm from "@/components/CoreAudiencesForm";
import WebsiteForm from "../WebsiteForm";
import { Alert } from "aha-components";
import deepEqual from "@/utils/deepEqual";
import Tracker from "@/utils/track";
import WebsiteLoading from "./WebsiteLoading";
import InfluencerUpgradeForm from "@/components/InfluencerUpgradeForm";
import { InfluencerUpgradeFormEnum } from "@/components/InfluencerUpgradeForm/config";
import BrandHighlightsEndorsementsForm from "@/components/BrandHighlightsEndorsementsForm";

import "./index.scss";
import BenchMarksForm from "./BenchMarksForm";

const { TextArea } = Input;
interface Props {
  ref: any;
  stage: CreateCampaignStageEnum;
  setNextLoading: Function;
  setNextDisabled: Function;
  details: any;
  isNextLoading: boolean;
}

const BusinessInformation: React.FC<Props> = forwardRef(
  ({ setNextLoading, stage, details, setNextDisabled, isNextLoading }, ref) => {
    const { t } = useTranslation();
    const [productForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const navigate = useNavigate();
    const campaignId = useQuery("campaignId");
    const { isSignedIn } = useUser();
    const { redirectToSignUp } = useClerk();
    const isActiveEdit = useQuery("isActiveEdit");

    const source = useQuery("source");
    const submitRes = async (isNext: boolean, isClose?: boolean) => {
      editForm
        .validateFields()
        .then(async (values: any) => {
          // 清理 coreAudienceAdvanced 对象，移除空值
          if (values.coreAudienceAdvanced) {
            const cleanedCoreAudience: Record<string, any> = {};
            Object.keys(values.coreAudienceAdvanced).forEach((key) => {
              const value = values.coreAudienceAdvanced[key];
              if (value !== undefined && value !== null && value !== "") {
                if (Array.isArray(value) && value.length > 0) {
                  cleanedCoreAudience[key] = value;
                } else if (!Array.isArray(value)) {
                  cleanedCoreAudience[key] = value;
                }
              }
            });
            values.coreAudienceAdvanced =
              Object.keys(cleanedCoreAudience).length > 0
                ? cleanedCoreAudience
                : {};
          }
          if (!!source) {
            Tracker.gtag("event", "ads_influencer_not_match_edit_change", {
              event_category: "ads_influencer_not_match",
              value: 1,
              source: source,
              campaign_id: campaignId,
              campaign_name: values?.productName,
              isChanged: !Object.keys(values).every((key) =>
                deepEqual(originProductInfo[key], values[key])
              ),
            });
          }
          if (
            !productInfo?.physicalProductPhoto &&
            productInfo?.productType === ProductType.PHYSICAL_PRODUCT &&
            !isOnlyColdEmail &&
            values?.physicalProductShipType === ProductPhysicalSelectEnum.ship
          ) {
            setShowPhotoError(true);
            return;
          }
          if (isClose && (!values?.website || !productInfo?.name)) {
            return;
          }
          try {
            setNextLoading(true);
            const params = {
              ...values,
              logo: productInfo?.logo,
              website: website?.startsWith("https://")
                ? website
                : `https://${website}`,
              name: editForm.getFieldValue("campaignName"),
              physicalProductPhoto: productInfo?.physicalProductPhoto,
              physicalProductPhotoName: productInfo?.physicalProductPhotoName,
              creatorAccountInfoFields:
                values?.creatorAccountNeeded ===
                InfluencerUpgradeFormEnum.ENABLE_ACCESS_ADD_CREDITS
                  ? values?.creatorAccountInfoFields
                  : undefined,
            };
            if (isSignedIn) {
              isNext
                ? await campaignSaveNextApi(campaignId, params, stage)
                : await campaignSaveApi(campaignId, params, stage);
              if (!isNext) {
                message.success(
                  t("campaignCreate.previewSampleMatches.saveSuccess")
                );
              } else {
                navigate(
                  `/campaign/create/${CreateCampaignStageEnum.SETTINGS}${window.location.search}`
                );
              }
            } else {
              await saveCampaignNoSessionApi(
                localStorage.getItem("tempOrgId") || "",
                campaignId,
                {
                  ...params,
                  requestType: isNext ? "next" : "save",
                  currentStep: stage,
                }
              );
              redirectToSignUp({
                redirectUrl: `/campaign/create/${
                  isNext ? CreateCampaignStageEnum.SETTINGS : stage
                }?campaignId=${campaignId}&isWebsiteSave=1`,
              });
            }
          } catch (error: any) {
            message.error(
              error?.message || t("campaignCreate.errors.networkError")
            );
          } finally {
            setNextLoading(false);
          }
        })
        .catch((error: any) => {
          if (!isClose) {
            message.error(`${error?.errorFields?.[0]?.errors?.[0]}`);
          }
        });
    };

    const onSubmit = () => {
      submitRes(true);
    };

    const handleSave = async (isClose = false) => {
      submitRes(false, isClose);
    };

    useImperativeHandle(ref, () => ({
      onFinish: () => onSubmit(),
      onSave: handleSave,
    }));

    // 获取产品信息
    const [website, setWebsite] = useState("");
    const [existWebsite, setExistWebsite] = useState("");
    const [productInfoLoading, setProductInfoLoading] = useState(false);
    const [websiteLoading, setWebsiteLoading] = useState(false);
    const [isShowStop, setShowStop] = useState(false);

    // 编辑产品信息
    const [productInfo, setProductInfo] = useState<any>(null);

    // 获取产品信息
    const getProductInfo = async () => {
      if (!campaignId) {
        return;
      }
      try {
        setNextLoading(true);
        const data = isSignedIn
          ? isActiveEdit
            ? await getActiveCampaignDetailApi(campaignId)
            : await getCampaignDetailApi(campaignId as string)
          : await getCampaignDetailNoSessionApi(
              localStorage.getItem("tempOrgId") || "",
              campaignId as string
            );
        return data;
      } catch (error) {
        // message.error(`${error}`);
      }
    };

    const [middleResult, setMiddleResult] = useState(null);

    // 轮询获取产品信息
    useEffect(() => {
      if (!productInfoLoading) {
        return;
      }
      const poll = async () => {
        try {
          const result: any = await getProductInfo();
          setMiddleResult(result?.data?.middleResult);
          if (
            result?.data?.product?.name &&
            !result?.data?.isOptimizingBusinessInfo
          ) {
            setProductInfo({
              ...result?.data?.product,
              productName: result?.data?.product?.name,
              campaignName: result?.data?.name,
            }); // 成功获取数据，保存数据
            editForm.setFieldsValue({
              ...result?.data?.product,
              productName: result?.data?.product?.name,
              campaignName: result?.data?.name,
              description: result?.data?.description,
              importantMatters: result?.data?.importantMatters,
            });
            setProductInfoLoading(false); // 请求成功，停止 loading
            setWebsiteLoading(false);
            setShowStop(false);
            setNextLoading(false);
            setIsOptimizingBusinessInfo(false);
          }
        } catch (error) {
          console.log(error);
        }
      };
      // 定义轮询的间隔
      const intervalId = setInterval(() => {
        if (productInfoLoading || isOptimizingBusinessInfo) {
          poll(); // 继续发起请求
        }
      }, 2000); // 每隔 2 秒发起一次请求

      // 如果数据请求成功，则清除轮询
      if (
        !productInfoLoading &&
        productInfo?.name &&
        !isOptimizingBusinessInfo
      ) {
        clearInterval(intervalId);
      }

      // 清理函数：组件卸载时清除轮询
      return () => {
        clearInterval(intervalId);
      };
    }, [productInfoLoading]);

    const [marketingStrategies, setMarketingStrategies] = useState<
      CampaignMarketingStrategy[]
    >([]);
    const [isOptimizingBusinessInfo, setIsOptimizingBusinessInfo] =
      useState(true);
    const [originProductInfo, setOriginProductInfo] = useState<any>(null);

    // 获取详情信息
    useEffect(() => {
      const {
        product,
        description,
        name,
        middleResult,
        marketingStrategies,
        importantMatters,
        creatorAccountNeeded,
        creatorAccountInfoFields
      } = details || {};
      setNextDisabled(true);
      setMarketingStrategies(marketingStrategies || []);
      // AI 重新生成产品信息中。。。
      setIsOptimizingBusinessInfo(details?.isOptimizingBusinessInfo);
      if (product?.website) {
        const website = product?.website?.replace("https://", "") || "";
        setExistWebsite(website);
        setWebsite(website);
        productForm.setFieldValue("website", website);
        if (!details?.isOptimizingBusinessInfo) {
          const values = {
            ...product,
            productName: product?.name,
            description,
            campaignName: name,
            importantMatters: importantMatters || "",
            creatorAccountNeeded,
            creatorAccountInfoFields,
          };
          editForm.setFieldsValue(values);
          setProductInfo(values);
          setOriginProductInfo(values);
          // 如果有值就默认展开
          if (
            product?.coreAudienceAdvanced?.gender?.length ||
            product?.coreAudienceAdvanced?.age?.length ||
            product?.coreAudienceAdvanced?.interest
          ) {
            setSettingShow(true);
          }
          if (!product?.physicalProductShipType) {
            editForm.setFieldValue(
              "physicalProductShipType",
              ProductPhysicalSelectEnum.ship
            );
          }
        }
        if (!product?.name || isOptimizingBusinessInfo) {
          setProductInfoLoading(true);
          setWebsiteLoading(true);
          setShowStop(true);
        }
        setMiddleResult(middleResult);
      }
    }, [details]);

    useEffect(() => {
      if (productInfo?.name) {
        setNextDisabled(false);
      } else {
        setNextDisabled(true);
      }
    }, [productInfo]);

    // handleFormValueChange
    const handleFormValueChange = (value: any) => {
      setProductInfo({
        ...productInfo,
        ...value,
      });
    };

    const [uploadLoading, setUploadLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const getAwsUrlAndUpload = async (file: any) => {
      setUploadLoading(true);
      if (file.type !== "image/svg+xml") {
        messageApi.open({
          type: "error",
          content: t("components.productLogo.youCanOnlyUploadSvgFile"),
          duration: 2,
        });
        return Promise.reject(
          new Error(t("components.productLogo.youCanOnlyUploadSvgFile"))
        );
      }
      // 大小大于10MB
      if (file.size > 10 * 1024 * 1024) {
        messageApi.open({
          type: "error",
          content: t("components.productLogo.fileSizeCannotExceed10MB"),
          duration: 2,
        });
        setUploadLoading(false);
        return Promise.reject(
          new Error(t("components.productLogo.fileSizeCannotExceed10MB"))
        );
      }

      try {
        const res = await getProductLogoUploadUrl({ key: file.name });
        const presignedUrl = res?.data?.presignedUrl;
        const publicUrl = res?.data?.publicUrl;
        const options = {
          headers: {
            "Content-Type": file.type,
          },
        };
        await axios.put(presignedUrl, file, options);
        setProductInfo({
          ...productInfo,
          logo: publicUrl,
        });
        setTimeout(() => {
          return Promise.resolve(presignedUrl);
        }, 0);
      } catch (error: any) {
        console.error(error);
        messageApi.open({
          type: "error",
          content:
            error?.message || t("components.productLogo.failedToUploadLogo"),
          duration: 2,
        });
      } finally {
        setUploadLoading(false);
      }
    };

    // Audiences settings
    const [settingShow, setSettingShow] = useState(false);

    // campaign name 的编辑态
    const [campaignEdit, setCampaignEdit] = useState(false);
    const [showPhotoError, setShowPhotoError] = useState(false);
    const isOnlyColdEmail =
      marketingStrategies?.length === 1 &&
      marketingStrategies[0] === CampaignMarketingStrategy.SALES_OUTREACH;

    const [caseInfo, setCaseInfo] = useState<any>({});

    return (
      <HeadContainer>
        <div className="flex flex-col gap-3">
          {(websiteLoading || productInfo?.name) && (
            <ProgressTimeLine
              step={CreateCampaignStageEnum.BUSINESS_INFORMATION}
              list={
                middleResult?.[CreateCampaignStageEnum.BUSINESS_INFORMATION]
              }
              isDone={!!productInfo?.name && !isOptimizingBusinessInfo}
              isShowExpand={true}
              isShowLoading={false}
            />
          )}
          <WebsiteForm
            details={details}
            stage={stage}
            setProductInfoLoading={setProductInfoLoading}
            setWebsiteLoading={setWebsiteLoading}
            setShowStop={setShowStop}
            websiteLoading={websiteLoading}
            isShowStop={isShowStop}
            setWebsite={setWebsite}
            website={website}
            productForm={productForm}
            existWebsite={existWebsite}
            setExistWebsite={setExistWebsite}
            setProductInfo={setProductInfo}
            setCaseInfo={setCaseInfo}
          />
        </div>
        {websiteLoading && !productInfo?.name && (
          <WebsiteLoading caseInfo={caseInfo} />
        )}
        {!productInfo?.name || isOptimizingBusinessInfo ? null : (
          <div className="w-full business-information mt-3 inline-flex flex-col justify-start items-start gap-4">
            <Alert
              className="w-full my-6 mt-0 mb-3"
              type="info"
              description={t(
                "campaignCreate.businessInformation.aiGeneratedDescription"
              )}
              closeable
            />
            <GenerateAnimation duration={5}>
              <div className="edit-product">
                <Form
                  name="productInfoForm"
                  form={editForm}
                  onFinish={onSubmit}
                  onValuesChange={handleFormValueChange}
                >
                  {/* campaignName */}
                  <div className="text-[#1c2024] text-[26px] font-semibold  leading-[42px] mb-6 ml-2">
                    {!campaignEdit ? (
                      <div className="flex items-center gap-2">
                        <span>{productInfo?.campaignName}</span>
                        <EditOutlined
                          className="text-[#A6A09B] text-[20px] cursor-pointer"
                          onClick={() => setCampaignEdit(true)}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Form.Item
                          name="campaignName"
                          className="mb-0 w-full"
                          rules={[
                            {
                              required: true,
                              message: t(
                                "campaignCreate.businessInformation.pleaseEnterCampaignName"
                              ),
                            },
                          ]}
                        >
                          <Input
                            placeholder={t(
                              "campaignCreate.businessInformation.pleaseEnterCampaignName"
                            )}
                            showCount
                            maxLength={50}
                            size="large"
                          />
                        </Form.Item>
                        <SaveOutlined
                          className="text-[#404040] text-[20px] cursor-pointer"
                          onClick={async () => {
                            try {
                              await editForm.validateFields(["campaignName"]);
                              setCampaignEdit(false);
                            } catch (error) {
                              console.log("error", error);
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {/* Basic information */}
                  <div className="form-card">
                    <div className="form-card-title">
                      {t(
                        "campaignCreate.businessInformation.basicInformation",
                        { defaultValue: "Basic Information" }
                      )}
                    </div>
                    <div className="product-basic-row mb-8">
                      <div className="product-basic-col">
                        <div className="col-title">
                          <span>
                            {t(
                              "campaignCreate.businessInformation.businessLogo",
                              { defaultValue: "Business Logo" }
                            )}
                          </span>
                        </div>
                        <div className="col-content">
                          <div className="col-content-logo">
                            <div className="logo-content">
                              <ProductLogo src={productInfo?.logo} size={80} />
                            </div>
                            <div className="logo-upload">
                              <Upload
                                listType="picture"
                                accept=".svg"
                                maxCount={1}
                                showUploadList={false}
                                className="avatar-uploader"
                                beforeUpload={getAwsUrlAndUpload}
                              >
                                <Button
                                  loading={uploadLoading}
                                  disabled={uploadLoading}
                                >
                                  {t(
                                    "campaignCreate.businessInformation.upload"
                                  )}
                                </Button>
                              </Upload>
                            </div>
                          </div>
                        </div>
                        <div className="col-desc">
                          {t(
                            "campaignCreate.businessInformation.logoFormatNote"
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="product-basic-row">
                      <div className="product-basic-col">
                        <div className="col-title">
                          <span>
                            {t(
                              "campaignCreate.businessInformation.businessName",
                              { defaultValue: "Business Name" }
                            )}
                          </span>
                          <span className="required">*</span>
                        </div>
                        <div className="col-content">
                          <div className="col-content-text">
                            <Form.Item
                              name="productName"
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    "campaignCreate.businessInformation.pleaseEnterBusinessName"
                                  ),
                                },
                              ]}
                            >
                              <Input
                                placeholder={t(
                                  "campaignCreate.businessInformation.pleaseEnterBusinessName"
                                )}
                                size="large"
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </div>
                      <div className="product-basic-col">
                        <div className="col-title">
                          <span>
                            {t(
                              "campaignCreate.businessInformation.productServiceType",
                              { defaultValue: "Product/Service Type" }
                            )}
                          </span>
                          <span className="required">*</span>
                        </div>
                        <div className="col-content">
                          <Form.Item
                            name="productType"
                            rules={[{ required: true }]}
                          >
                            <Select
                              style={{ width: "100%" }}
                              size="large"
                              options={clientTypeOptions}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                    <BenchMarksForm details={details} name="benchmarks" />
                    {editForm.getFieldValue("productType") ===
                    ProductType.PHYSICAL_PRODUCT ? (
                      <ProductDelivery
                        form={editForm}
                        productInfo={productInfo}
                        setProductInfo={setProductInfo}
                        showPhotoError={showPhotoError}
                        setShowPhotoError={setShowPhotoError}
                      />
                    ) : (
                      <InfluencerUpgradeForm
                        value={details?.creatorAccountNeeded}
                      />
                    )}
                  </div>
                  {/* Campaign Introduction */}
                  <div className="form-card">
                    <div className="form-card-title">
                      {t(
                        "campaignCreate.businessInformation.businessIntroduction",
                        { defaultValue: "Business introduction" }
                      )}
                    </div>
                    <div className="product-basic-row">
                      <div className="product-basic-col">
                        <div className="col-content-text">
                          <Form.Item
                            name="intro"
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: t(
                                  "campaignCreate.businessInformation.pleaseEnterBusinessIntroduction"
                                ),
                              },
                            ]}
                          >
                            <TextArea
                              placeholder={t(
                                "components.matchInfluencerAdjustModal.introPlaceholder"
                              )}
                              size="large"
                              autoSize={{ minRows: 3, maxRows: 5 }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Brand highlights & endorsements */}
                  <BrandHighlightsEndorsementsForm
                    form={editForm}
                    name="endorsement"
                  />
                  {/* Important Matters */}
                  {/* <div className="form-card">
                    <div className="form-card-title">
                      {t(
                        "campaignCreate.businessInformation.importantMatters"
                      )}
                    </div>
                    <div className="product-basic-row">
                      <div className="product-basic-col">
                        <div className="col-title">
                          {t(
                            "campaignCreate.businessInformation.importantMattersDescription"
                          )}
                        </div>
                        <div className="col-content-text">
                          <Form.Item
                            name="importantMatters"
                          >
                            <TextArea
                              placeholder={t('campaignCreate.businessInformation.importantMattersPlaceholder')}
                              size="large"
                              autoSize={{ minRows: 3, maxRows: 5 }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  </div> */}
                  {/* Core Functions and Talking Points */}
                  <div className="form-card">
                    <div className="form-card-title">
                      {t(
                        "campaignCreate.businessInformation.coreSellingPoints"
                      )}
                    </div>
                    <CoreSellingPointsForm />
                  </div>
                  {/* Core product audiences */}
                  <div className="product-basic-col form-card">
                    <div className="col-title">
                      <span className="form-card-title">
                        {t("campaignCreate.businessInformation.coreAudiences")}
                      </span>
                      <span className="required">*</span>
                    </div>
                    <CoreAudiencesForm name="coreAudience" form={editForm} />
                  </div>
                  {/* <div
                    className={`product-audience-settings form-card ${
                      settingShow ? "product-audience-settings-show" : ""
                    }`}
                  >
                    <div
                      className="product-audience-settings-title"
                      onClick={() => setSettingShow(!settingShow)}
                    >
                      <span
                        className={`form-card-title-fold ${settingShow ? "mb-6" : "mb-0"
                          }`}
                      >
                        {t(
                          "campaignCreate.businessInformation.advancedSettings"
                        )}
                      </span>
                      <RightArrowIcon className="title-icon" />
                    </div>
                    <div className={`product-audience-settings-content`}>
                      <div className="product-basic-row">
                        <div className="product-basic-col">
                          <div className="col-title">
                            <span>
                              {t("campaignCreate.businessInformation.gender")}
                            </span>
                          </div>
                          <div className="col-content">
                            <Form.Item
                              name={["coreAudienceAdvanced", "gender"]}
                            >
                              <Select
                                mode="multiple"
                                size="large"
                                allowClear
                                placeholder={t(
                                  "campaignCreate.businessInformation.pleaseSelectGender"
                                )}
                                options={genderOptions}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className="product-basic-col">
                          <div className="product-basic-col">
                            <div className="col-title">
                              <span>
                                {t("campaignCreate.businessInformation.age")}
                              </span>
                            </div>
                            <div className="col-content">
                              <Form.Item name={["coreAudienceAdvanced", "age"]}>
                                <Select
                                  mode="multiple"
                                  size="large"
                                  allowClear
                                  placeholder={t(
                                    "campaignCreate.businessInformation.pleaseSelectAge"
                                  )}
                                  options={ageOptions}
                                />
                              </Form.Item>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="product-basic-row">
                        <div className="product-basic-col">
                          <div className="col-title">
                            <span>
                              {t("campaignCreate.businessInformation.interest")}
                            </span>
                          </div>
                          <div className="col-content">
                            <Form.Item
                              name={["coreAudienceAdvanced", "interest"]}
                            >
                              <TextArea
                                placeholder={t(
                                  "campaignCreate.businessInformation.pleaseEnterInterests"
                                )}
                                size="large"
                                autoSize={{ minRows: 1, maxRows: 5 }}
                                maxLength={200}
                                showCount
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </Form>
              </div>
            </GenerateAnimation>
          </div>
        )}
      </HeadContainer>
    );
  }
);
export default BusinessInformation;
